import { CONFIRM_QUIT_MODAL_WIDTH } from '@/ui/components/modals/constants';
import { computed, defineComponent, h, ref, getCurrentInstance } from 'vue';
import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
import { pushRoute } from '@/ui/helpers';
import { button } from '@/ui/components';
import { EventBus } from '@/ui/EventBus';
import { LegacyChecklistRecord } from '@/ui/pages/inspection/LegacyChecklistRecord';
import { t } from '@/ui/i18n/i18n';
import { Events } from '@/ui/events';
import { INSPECTION_STATUS } from '@/_legacy/constants';
import { NamedLocation } from '@/ui/routes/types';
import InspectionTitle from '@/ui/components/inspection/InspectionTitle.vue';

interface InspectionNavbarProps {
  record: LegacyChecklistRecord;
  loading?: boolean;
  saving?: boolean;
  submitting?: boolean;
}

export const InspectionNavbar = defineComponent<InspectionNavbarProps>({
  name: 'InspectionNavbar',
  props: ['record', 'loading', 'saving', 'submitting'],
  setup(props) {
    const isQuitModalOpen = ref(false);
    const instance = getCurrentInstance();

    const emitSubmittedDraft = () => {
      EventBus.$emit(Events.submittedDraftInspection, {
        inspection: instance?.proxy?.$store?.getters?.['conduct/inspection'],
      });
    };

    const isDraft = computed(() => props.record?.status === INSPECTION_STATUS.DRAFT);
    const isSubmitted = computed(() => Boolean(props.record?.submitted_id));
    const cancelRoute = computed<NamedLocation>(
      () => isDraft.value ? { name: 'forms', query: { tab: 'draft' } } : { name: 'forms' },
    );

    const saveDraftAndQuit = () => {
      if (props.loading) {
        return;
      }

      if (isDraft.value || !isSubmitted.value) {
        emitSubmittedDraft();

        return;
      }

      pushRoute(cancelRoute.value);
    };

    const submitInspection = () => {
      if (props.loading) {
        return;
      }

      EventBus.$emit(Events.clickedSubmitInspectionButton);
    };

    return function render() {
      return h('div', [
        h(ConfirmationModal, {
          props: {
            customClass: 'create-inspection-page__leave-modal',
            width: `${CONFIRM_QUIT_MODAL_WIDTH}px`,
            hideClose: false,
            active: isQuitModalOpen.value,
            declineButtonProps: { btnClass: 'create-inspection-page__navbar-button', outlined: true, type: 'is-primary' },
            declineText: isDraft.value || !isSubmitted.value ? t('inspections.save-and-quit') : t('common.yes-quit'),
            confirmText: isDraft.value || !isSubmitted.value ? t('common.quit') : t('common.stay-on-page'),
          },
          on: {
            close: () => { isQuitModalOpen.value = false; },
            declined: () => {
              saveDraftAndQuit();
            },
            accepted: () => {
              if (isDraft.value || !isSubmitted.value) {
                pushRoute(cancelRoute.value);

                return;
              }

              isQuitModalOpen.value = false;
            },
          },
        },
        [t('inspections.quit-inspection-page-new')]),
        h('div', { class: 'create-inspection-page__navbar' }, [
          h(InspectionTitle, { props: { record: props.record } }),
          h('div', { class: 'flex' }, [
            button(
              t('common.quit'),
              () => { isQuitModalOpen.value = true; },
              undefined,
              { btnClass: 'create-inspection-page__navbar-button', outlined: true, disabled: props.loading },
            ),
            !isSubmitted.value || isDraft.value ? button(
              t('inspections.save-and-quit'),
              () => saveDraftAndQuit(),
              undefined,
              {
                btnClass: 'create-inspection-page__navbar-button',
                outlined: true,
                disabled: props.loading,
                loading: props.saving,
              },
            ) : null,
            button(
              t('common.submit'),
              () => submitInspection(),
              undefined,
              { disabled: props.loading, loading: props.submitting },
            ),
          ]),
        ]),
      ]);
    };
  },
});
